/* eslint-disable react/prop-types */
import { ArrowLeft, ArrowRight, Calendar } from '@sds-system/assets';
import type { ChangeEvent, ChangeEventHandler } from 'react';
import { forwardRef, useEffect, useRef, useState } from 'react';
import type { DatePickerProps as ModuleDatePickerProps } from 'react-datepicker';
import ReactDatePicker from 'react-datepicker';

import { Button } from '../Button';
import { Column } from '../Column';
import { Icon } from '../Icon';
import { IconButton } from '../IconButton';
import { Typography } from '../Typography';
import { formatDate, handleChangeFormatDate, weekLabel } from './DatePicker.const';
import {
  StyledDatePickerEnterWrapper,
  StyledDatePickerHeader,
  StyledDatePickerRoot,
  StyledDatePickerWrapper,
} from './DatePicker.styled';
import type {
  CustomInputProps,
  DatePickerChangeHandler,
  DatePickerProps,
  DateRangeChangeHandler,
} from './DatePicker.types';

export const DatePicker = <T extends boolean = false>({
  size = 'L',
  disabled = false,
  styledComponents,
  isConfirmMode,
  onChange,
  date,
  isRangeMode,
  isEnterMode = false,
  errorMessages = [],
  isError = false,
}: DatePickerProps<T>) => {
  const datePickerDate = isRangeMode ? date || [null, null] : date || null;

  const resetDate = useRef<(Date | null) | [Date | null, Date | null]>(datePickerDate);
  const enterModeFromDate = useRef<HTMLInputElement>(null);
  const enterModeToDate = useRef<HTMLInputElement>(null);
  const [currentDate, setCurrentDate] = useState<(Date | null) | [Date | null, Date | null]>(
    datePickerDate,
  );
  const [isActive, setIsActive] = useState<boolean>(false);

  const isFilled = isRangeMode ? !!(currentDate as [Date | null, Date | null])?.[1] : !!currentDate;

  const changeHandler: DatePickerChangeHandler = (date) => {
    setCurrentDate(date as Date & [Date, Date]);
    if (!isConfirmMode) setIsActive(false);
    if (!isConfirmMode && !isRangeMode && onChange) (onChange as DatePickerChangeHandler)(date);
  };

  const changeRangeHandler: DateRangeChangeHandler = (dates) => {
    setCurrentDate(dates as Date & [Date, Date]);
    if (!isConfirmMode && isRangeMode && onChange) (onChange as DateRangeChangeHandler)(dates);
    if (!isConfirmMode && dates?.[1]) setIsActive(false);
  };

  const backSpaceHandler = (e: ChangeEvent<HTMLInputElement>, input: string) => {
    if (
      input.slice(-1) === '.' &&
      (e?.nativeEvent as InputEvent).inputType === 'deleteContentBackward'
    ) {
      input = input.slice(0, input.length - 1);
    }

    e.target.value = input;
  };

  const inputEnterChangeHandler: ChangeEventHandler<HTMLInputElement> = (e) => {
    const input = e.target.value.replace(/\D/g, '');
    const formattedDate = handleChangeFormatDate(input);
    backSpaceHandler(e, formattedDate);

    if (formattedDate.length === 10) {
      const newDate = new Date(formattedDate);

      if (isRangeMode) {
        const date = [newDate, (currentDate as [Date | null, Date | null])?.[1]] as [
          Date | null,
          Date | null,
        ];
        setCurrentDate(date);
        if (date[0] && date[1]) (onChange as DateRangeChangeHandler)?.(date);
      } else {
        setCurrentDate(newDate);
        (onChange as DatePickerChangeHandler)?.(newDate);
      }
    }
  };

  const inputEnterRangeChangeHandler: ChangeEventHandler<HTMLInputElement> = (e) => {
    const input = e.target.value.replace(/\D/g, '');
    const formattedDate = handleChangeFormatDate(input);
    backSpaceHandler(e, formattedDate);

    if (currentDate && formattedDate.length === 10) {
      const endDate = new Date(formattedDate);

      const date = [(currentDate as [Date | null, Date | null])?.[0], endDate] as [
        Date | null,
        Date | null,
      ];
      setCurrentDate(date);
      if (date[0] && date[1]) (onChange as DateRangeChangeHandler)?.(date);
    }
  };

  const clickInputHandler = () => {
    setIsActive(true);
  };

  const clickOutsideHandler = () => {
    if (isConfirmMode) {
      setCurrentDate(resetDate.current);
    }
    setIsActive(false);
  };

  const clickConfirmHandler = () => {
    if (!isConfirmMode) return;

    resetDate.current = currentDate;
    if (onChange) onChange(currentDate as Date & [Date, Date]);
    setIsActive(false);
  };

  console.log(!!currentDate || currentDate?.[1] !== null);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any

  const CustomInput = forwardRef<HTMLInputElement, CustomInputProps>(({ value, onClick }, ref) => (
    <StyledDatePickerWrapper
      disabled={disabled}
      errorMessages={errorMessages}
      isActive={isActive}
      isError={isError}
      isFilled={isFilled}
      onClick={onClick}
      ref={ref}
      size={size}
      {...styledComponents?.input}
    >
      <input placeholder={isRangeMode ? 'yyyy.mm.dd - yyyy.mm.dd' : 'yyyy.mm.dd'} value={value} />
      {!isEnterMode && (
        <Icon
          color={!disabled ? 'border.interactive.primary' : 'border.disabled'}
          size='XS'
          source={Calendar}
        />
      )}
    </StyledDatePickerWrapper>
  ));

  const DateRangeProps: ModuleDatePickerProps = {
    onClickOutside: clickOutsideHandler,
    onInputClick: clickInputHandler,
    dateFormat: 'yyyy/MM/dd',
    formatWeekDay: (nameOfDay) => weekLabel[nameOfDay] || '',
    open: !disabled && isActive,
    showPopperArrow: false,
    children: isConfirmMode && (
      <Button
        onClick={clickConfirmHandler}
        size='M'
        styledComponents={{
          root: { mt: 4, css: { marginLeft: 'auto' } },
        }}
      >
        적용
      </Button>
    ),
    customInput: <CustomInput />,
    renderCustomHeader: ({ increaseMonth, decreaseMonth, monthDate }) => {
      return (
        <StyledDatePickerHeader {...styledComponents?.header}>
          <IconButton
            onClick={() => {
              decreaseMonth();
            }}
            size='L'
            variant='ghost.assistive'
          >
            <Icon source={ArrowLeft} />
          </IconButton>
          <Typography fontType='header3.medium'>
            {monthDate
              .toLocaleString('ko-KR', {
                month: '2-digit',
                year: 'numeric',
              })
              .slice(0, -1)}
          </Typography>
          <IconButton
            onClick={() => {
              increaseMonth();
            }}
            size='L'
            variant='ghost.assistive'
          >
            <Icon source={ArrowRight} />
          </IconButton>
        </StyledDatePickerHeader>
      );
    },
  };

  useEffect(() => {
    if (date) {
      if (isEnterMode) {
        if (enterModeFromDate?.current)
          enterModeFromDate.current.value = Array.isArray(date)
            ? formatDate(date[0] as Date)
            : formatDate(date);
        if (enterModeToDate?.current)
          enterModeToDate.current.value = date
            ? formatDate((date as [Date | null, Date | null])?.[1] as Date)
            : '';
      }
      if (onChange) onChange(date as Date & [Date, Date]);
      setCurrentDate(date as Date & [Date, Date]);
    }
  }, []);

  return (
    <StyledDatePickerRoot {...styledComponents?.root} styledDatePickerDay={styledComponents?.day}>
      {!isEnterMode && (
        <>
          {isRangeMode ? (
            <ReactDatePicker
              {...DateRangeProps}
              endDate={(currentDate as [Date, Date])?.[1] || undefined}
              onChange={changeRangeHandler}
              selectsRange
              startDate={(currentDate as [Date, Date])?.[0] || undefined}
            />
          ) : (
            <ReactDatePicker
              {...DateRangeProps}
              onChange={changeHandler}
              selected={currentDate as Date}
            />
          )}
        </>
      )}
      {isEnterMode && (
        <StyledDatePickerEnterWrapper
          disabled={disabled}
          errorMessages={errorMessages}
          isActive={isActive}
          isError={isError}
          isFilled={isFilled}
          isRangeMode={isRangeMode}
          size={size}
          {...styledComponents?.input}
        >
          <input
            onBlur={() => setIsActive(false)}
            onChange={inputEnterChangeHandler}
            onFocus={() => setIsActive(true)}
            placeholder='yyyy.mm.dd'
            ref={enterModeFromDate}
            type='text'
          />
          {isRangeMode && (
            <>
              <Typography>-</Typography>
              <input
                defaultValue=''
                onBlur={() => setIsActive(false)}
                onChange={inputEnterRangeChangeHandler}
                onFocus={() => setIsActive(true)}
                placeholder='yyyy.mm.dd'
                ref={enterModeToDate}
                type='text'
              />
            </>
          )}
        </StyledDatePickerEnterWrapper>
      )}
      {errorMessages.length > 0 && (
        <Column gap={4} mt={8}>
          {errorMessages.map((message) => (
            <Typography color='text.error' fontType='caption.regular' key={message}>
              {message}
            </Typography>
          ))}
        </Column>
      )}
    </StyledDatePickerRoot>
  );
};
